/**
 * Demo-mode bridge. Deterministic, instant-ish, charges a realistic small fee
 * so the "fees come out of the round-up" accounting is exercised for real.
 */
import type { Hex } from "../types";
import type { BridgeAdapter, BridgeQuote, BridgeResult } from "./types";

const FEE_BPS = 30n; // 0.30%
const FLAT_FEE_UNITS = 10_000n; // $0.01 in 6dp
const MIN_INPUT_UNITS = 250_000n; // $0.25

function pseudoTxHash(seed: string): Hex {
  let h = 0n;
  for (const c of seed) h = (h * 131n + BigInt(c.charCodeAt(0))) % (1n << 256n);
  return `0x${h.toString(16).padStart(64, "0")}` as Hex;
}

export class MockBridge implements BridgeAdapter {
  name = "demo-bridge";
  /** Submitted-but-unresolved outputs; lost on reload → failed-retryable. */
  private inFlight = new Map<Hex, bigint>();

  async getQuote(inputUnits: bigint): Promise<BridgeQuote> {
    const pctFee = (inputUnits * FEE_BPS) / 10_000n;
    const feeUnits = pctFee + FLAT_FEE_UNITS;
    const out = inputUnits > feeUnits ? inputUnits - feeUnits : 0n;
    return {
      inputUnits,
      expectedOutputUnits: out,
      feeUnits,
      minInputUnits: MIN_INPUT_UNITS,
      estimatedSeconds: 2,
      quoteId: `demo-${inputUnits}`,
      expiresAt: Date.now() + 60_000,
    };
  }

  async submit(quote: BridgeQuote): Promise<Hex> {
    const txHash = pseudoTxHash(`bridge:${quote.quoteId}:${Date.now()}`);
    this.inFlight.set(txHash, quote.expectedOutputUnits);
    return txHash;
  }

  async waitForCompletion(txHash: Hex): Promise<BridgeResult> {
    const output = this.inFlight.get(txHash);
    if (output === undefined) {
      throw new Error("Demo bridge state lost (reload) — retry the round-up");
    }
    await new Promise((r) => setTimeout(r, 1200));
    this.inFlight.delete(txHash);
    return { txHash, outputUnits: output };
  }
}
